import { useNavigate } from 'react-router-dom'
import { useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ArrowUp, ArrowDown, Minus, LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

// Stable string → short id (deterministic, no re-register on every render).
// Replaces the previous Math.random() approach that caused sparkline gradient
// flicker on every metric refresh.
function stableHashId(input: string): string {
  let h = 0
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) - h) + input.charCodeAt(i)
    h |= 0
  }
  return `spk-${Math.abs(h).toString(36)}`
}

interface MetricCardProps {
  title: string
  value: string | number
  change?: number | null
  changeLabel?: string
  comparisonValue?: string | number
  icon?: LucideIcon
  iconColor?: string
  trend?: 'up' | 'down' | 'down-bad' | 'neutral'
  sparklineData?: number[]
  loading?: boolean
  compact?: boolean
  href?: string
}

export default function MetricCard({
  title,
  value,
  change,
  changeLabel = 'vs last period',
  comparisonValue,
  icon: Icon,
  iconColor = 'text-brand-primary',
  trend,
  sparklineData,
  loading = false,
  compact = false,
  href
}: MetricCardProps) {
  const navigate = useNavigate()
  const getTrendIcon = () => {
    if (trend === 'up') return <ArrowUp className="h-3 w-3" />
    if (trend === 'down' || trend === 'down-bad') return <ArrowDown className="h-3 w-3" />
    return <Minus className="h-3 w-3" />
  }

  const getTrendColor = () => {
    if (trend === 'up') return 'text-emerald-600 dark:text-emerald-400'
    if (trend === 'down-bad') return 'text-red-600 dark:text-red-400'
    if (trend === 'down') return 'text-muted-foreground'
    return 'text-muted-foreground'
  }

  const getTrendBgColor = () => {
    if (trend === 'up') return 'bg-emerald-500/10'
    if (trend === 'down-bad') return 'bg-red-500/10'
    if (trend === 'down') return 'bg-muted'
    return 'bg-muted'
  }

  // Stable sparkline gradient id keyed on title + trend — no flicker on
  // metric refresh. Memoized because title/trend rarely change.
  const gradientId = useMemo(() => stableHashId(`${title}-${trend ?? ''}`), [title, trend])

  // Improved sparkline SVG with gradient fill
  const renderSparkline = () => {
    if (!sparklineData || sparklineData.length < 2) return null

    const width = 80
    const height = 24
    const max = Math.max(...sparklineData)
    const min = Math.min(...sparklineData)
    const range = max - min || 1

    const points = sparklineData.map((value, index) => {
      const x = (index / (sparklineData.length - 1)) * width
      const y = height - ((value - min) / range) * (height - 4) - 2
      return { x, y }
    })

    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ')
    const areaPath = `${linePath} L ${width},${height} L 0,${height} Z`

    const strokeColor = trend === 'down' ? '#ef4444' : trend === 'up' ? '#10b981' : '#3b82f6'
    const fillColor = trend === 'down' ? '#ef4444' : trend === 'up' ? '#10b981' : '#3b82f6'

    return (
      <svg width={width} height={height} className="overflow-visible">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={fillColor} stopOpacity={0.3} />
            <stop offset="100%" stopColor={fillColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path
          d={areaPath}
          fill={`url(#${gradientId})`}
        />
        <path
          d={linePath}
          fill="none"
          stroke={strokeColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* End point dot */}
        <circle
          cx={points[points.length - 1].x}
          cy={points[points.length - 1].y}
          r="2"
          fill={strokeColor}
        />
      </svg>
    )
  }

  if (loading) {
    return (
      <Card className={cn(compact && 'shadow-sm')}>
        <CardHeader className={cn(
          'flex flex-row items-center justify-between space-y-0',
          compact ? 'pb-1 pt-3 px-3' : 'pb-2'
        )}>
          <CardTitle className="text-sm font-medium">
            <div className="h-4 w-20 bg-muted animate-pulse rounded" />
          </CardTitle>
          {Icon && (
            <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
          )}
        </CardHeader>
        <CardContent className={compact ? 'px-3 pb-3' : ''}>
          <div className="h-7 w-24 bg-muted animate-pulse rounded mb-2" />
          <div className="h-4 w-32 bg-muted animate-pulse rounded" />
        </CardContent>
      </Card>
    )
  }

  // Trend-based left edge accent — meaningful (up/down) trends get color,
  // neutral is truly neutral (no edge) so every card isn't tinted.
  const getAccentColor = () => {
    if (trend === 'up') return 'border-l-emerald-500'
    if (trend === 'down-bad') return 'border-l-red-500'
    if (trend === 'down') return 'border-l-muted-foreground/20'
    return 'border-l-transparent'
  }

  return (
    <Card
      className={cn(
        // Targeted transitions (no `transition: all`) with a strong ease-out
        // curve. Hover lift is subtle — this card renders 100+ times/session,
        // so the feedback must be understated, not showy.
        'border-l-[2px] [transition:transform_160ms_cubic-bezier(0.23,1,0.32,1),box-shadow_200ms_ease-out]',
        'hover:[@media(hover:hover)_and_(pointer:fine)]:shadow-sm',
        'active:scale-[0.98]',
        getAccentColor(),
        compact && 'shadow-sm',
        href && 'cursor-pointer'
      )}
      onClick={href ? () => navigate(href) : undefined}
    >
      <CardHeader className={cn(
        'flex flex-row items-center justify-between space-y-0',
        compact ? 'pb-1 pt-3 px-3' : 'pb-2'
      )}>
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        {Icon && (
          <div className={cn(
            'rounded-full p-1.5',
            // No hover-scale — icons here are seen on every session load;
            // per-metric animation cost outweighs the delight.
            iconColor.replace('text-', 'bg-').replace('-500', '-500/10').replace('-primary', '-primary/10').replace('-secondary', '-secondary/10')
          )}>
            <Icon className={cn('h-4 w-4', iconColor)} strokeWidth={2} />
          </div>
        )}
      </CardHeader>
      <CardContent className={compact ? 'px-3 pb-3' : ''}>
        <div className="flex items-baseline gap-2">
          <span className={cn(
            // Tabular-nums + Geist lets digits align visually across stacked
            // cards. Font weight moved from extrabold to semibold — hierarchy
            // comes from weight contrast + size, not shout volume.
            'font-semibold tracking-tight tabular-nums',
            compact ? 'text-xl' : 'text-3xl'
          )}>
            {value}
          </span>
          {comparisonValue !== undefined && (
            <span className="text-sm text-muted-foreground tabular-nums">
              / {comparisonValue}
            </span>
          )}
        </div>

        {/* Change indicator and sparkline row */}
        {(change != null || sparklineData) && (
          <div className="flex items-center justify-between gap-2 mt-2">
            {change != null && (
              <div className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                getTrendBgColor(),
                getTrendColor()
              )}>
                {getTrendIcon()}
                <span>{Math.abs(Math.round(change))}%</span>
                {changeLabel && (
                  <span className="text-muted-foreground font-normal ml-1 hidden sm:inline">
                    {changeLabel}
                  </span>
                )}
              </div>
            )}
            {sparklineData && sparklineData.length > 1 && (
              <div className="ml-auto opacity-70 group-hover:opacity-100 transition-opacity">
                {renderSparkline()}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
